import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, ConfirmDialog, useToast } from '@kss/ui';
import { ChevronDown, ChevronRight, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { categoryService, type Category } from '@kss/api';
import { encodeId } from '../../utils/idCodec';

interface TreeNode extends Category {
  depth: number;
  childNodes: TreeNode[];
}

function buildTree(rows: Category[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  rows.forEach((r) => {
    if (!r.categoryId) return;
    byId.set(r.categoryId, { ...r, depth: 0, childNodes: [] });
  });
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    if (node.parentId && byId.has(node.parentId)) {
      const parent = byId.get(node.parentId)!;
      node.depth = parent.depth + 1;
      parent.childNodes.push(node);
    } else {
      roots.push(node);
    }
  });
  // Recompute depth top-down to handle out-of-order rows.
  const recomputeDepth = (n: TreeNode, d: number) => {
    n.depth = d;
    n.childNodes.forEach((c) => recomputeDepth(c, d + 1));
  };
  roots.forEach((r) => recomputeDepth(r, 0));
  // Stable sort by sortOrder then name at every level.
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.childNodes));
  };
  sortRec(roots);
  return roots;
}

export default function AllCategories() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [rows, setRows] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const data = await categoryService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setErr(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const tree = useMemo(() => buildTree(rows), [rows]);

  function toggle(id: string) {
    setExpanded((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function onDelete() {
    if (!confirmDelete?.categoryId) return;
    setDeleting(true);
    try {
      await categoryService.delete(confirmDelete.categoryId);
      toast.success('Deleted', `${confirmDelete.name} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally { setDeleting(false); }
  }

  function renderNode(node: TreeNode): React.ReactNode {
    const id = node.categoryId!;
    const hasKids = node.childNodes.length > 0;
    const isOpen = expanded.has(id);
    return (
      <div key={id}>
        <div
          className="group flex items-center gap-2 border-b border-border py-1.5 px-2 hover:bg-surface-card-hover"
          style={{ paddingLeft: `${node.depth * 24 + 8}px` }}
        >
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center text-content-muted hover:text-content-primary"
            onClick={() => hasKids && toggle(id)}
            disabled={!hasKids}
            aria-label={hasKids ? (isOpen ? 'Collapse' : 'Expand') : 'No children'}
          >
            {hasKids ? (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="h-1 w-1 rounded-full bg-border" />}
          </button>
          <span className="flex-1 text-sm font-medium text-content-primary">{node.name}</span>
          {node.description && (
            <span className="hidden truncate text-xs text-content-muted md:inline">{node.description}</span>
          )}
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${node.isActive ? 'bg-success/10 text-success' : 'bg-content-muted/10 text-content-muted'}`}>
            {node.isActive ? 'Active' : 'Inactive'}
          </span>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/panel/categories/new?parent=${encodeURIComponent(id)}`)}
              title="Add child"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/panel/categories/${encodeId(id)}`)}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDelete(node)}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {hasKids && isOpen && node.childNodes.map(renderNode)}
      </div>
    );
  }

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-content-primary">Category tree</h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpanded(new Set(rows.map((r) => r.categoryId!).filter(Boolean)))}
            >
              Expand all
            </Button>
            <Button variant="outline" size="sm" onClick={() => setExpanded(new Set())}>
              Collapse all
            </Button>
            <Link to="/panel/categories/new">
              <Button size="sm"><Plus className="h-3.5 w-3.5" /> New category</Button>
            </Link>
          </div>
        </div>
        {tree.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-content-muted">
            No categories yet. Click <strong>New category</strong> to add a top-level entry.
          </div>
        ) : (
          <div className="rounded-md border border-border">{tree.map(renderNode)}</div>
        )}
      </Card>
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={onDelete}
        title="Delete category?"
        message={`Permanently remove "${confirmDelete?.name ?? ''}"? Children become orphans (parentId set to none).`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}
